/**
 * Shared visual language for both maps and the panels, and the theme system.
 *
 * UI only — nothing here is imported by src/sim/.
 *
 * THEMES ARE ONE SOURCE OF TRUTH IN TWO FORMATS. The panels are HTML and read
 * colours through CSS custom properties; the maps are canvas and read them from
 * plain objects at draw time. `applyTheme` writes both, so a theme change
 * re-hues the maps as well as the chrome — the world map fills its hexes with
 * the accent colour, which is why switching theme visibly repaints the planet
 * rather than just the frame around it.
 *
 * COLORS, MINERAL_COLORS and BUILDING_COLORS are deliberately MUTABLE. Drawing
 * code reads them every frame, so mutating them in place is what lets a theme
 * change reach the canvas without every caller subscribing to something.
 */

import type { BuildingId, MineralId } from '../sim/data';

export interface Palette {
  /** Shown on the theme button. */
  name: string;
  background: string;
  panel: string;
  /** A second surface, for rows and wells inside a panel. */
  panelAlt: string;
  border: string;
  gridLine: string;
  text: string;
  textDim: string;
  accent: string;
  /** A muted accent for the filled state of toggles and lit rows. */
  accentSoft: string;
  warning: string;
  danger: string;
  ok: string;
  factory: string;
  empty: string;
  queued: string;
  building: string;
  demolishing: string;
  /**
   * Six hues, one per console screen, in rail order.
   *
   * A theme used to be a single accent against a lot of grey, which made every
   * screen look the same and gave the eye nothing to navigate by. Each screen
   * now owns a colour — its rail key, its lamp, its title and its panel headers
   * — so "the green one" is a usable way to think about the Colony screen. They
   * are chosen to sit within the theme's temperature rather than to be a
   * rainbow: Ember's six are all warm, Verdant's all cool.
   */
  hues: readonly string[];
  minerals: Record<MineralId, string>;
  buildings: Record<BuildingId, string>;
}

/**
 * Minerals for the dark themes, spec §4.1 order. Kept roughly evocative —
 * hematite rust-red, water ice pale blue, sylvite salt-white — so a stacked bar
 * can be read without constantly checking the legend. Shared across every dark
 * theme: a mineral's colour is its identity, not the theme's decoration.
 */
const DARK_MINERALS: Record<MineralId, string> = {
  hematite: '#b0413e',
  quartz: '#d8d2c4',
  bauxite: '#c07f4a',
  chalcopyrite: '#c9a227',
  waterIce: '#7fb8d9',
  carbonate: '#9aa5a0',
  apatite: '#6f9e73',
  sylvite: '#e8e4dd',
};

/** One colour per building, spec §10 order. */
const DARK_BUILDINGS: Record<BuildingId, string> = {
  habitation: '#4f9d69',
  farm: '#8bc34a',
  waterPlant: '#4a9fd8',
  powerPlant: '#e3a13a',
  factoryDistrict: '#9a7bd0',
  recreation: '#d9689a',
};

export const THEMES: readonly Palette[] = [
  {
    name: 'Helios',
    hues: [
      '#2f81f7',
      '#38b2c4',
      '#e3b341',
      '#4fb872',
      '#a97fe8',
      '#e06c9f',
    ],
    background: '#0e1116',
    panel: '#161b22',
    panelAlt: '#1b222c',
    border: '#2a323d',
    gridLine: '#222a34',
    text: '#c9d1d9',
    textDim: '#6e7781',
    accent: '#2f81f7',
    accentSoft: '#17324f',
    warning: '#d29922',
    danger: '#f85149',
    ok: '#3fb950',
    factory: '#e3b341',
    empty: '#1b2129',
    queued: '#30363d',
    building: '#1f6feb',
    demolishing: '#f85149',
    minerals: DARK_MINERALS,
    buildings: DARK_BUILDINGS,
  },
  {
    name: 'Ember',
    hues: [
      '#ff8c42',
      '#ffd166',
      '#ef5b5b',
      '#c2a35a',
      '#e0785a',
      '#d98fb0',
    ],
    background: '#14100c',
    panel: '#1e1813',
    panelAlt: '#261e17',
    border: '#3d3025',
    gridLine: '#2e2419',
    text: '#efdfc9',
    textDim: '#9c8770',
    accent: '#ff8c42',
    accentSoft: '#4a2a12',
    warning: '#ffc857',
    danger: '#ef5b5b',
    ok: '#9ac95f',
    factory: '#ffd166',
    empty: '#221b14',
    queued: '#3a2e22',
    building: '#ff8c42',
    demolishing: '#ef5b5b',
    minerals: DARK_MINERALS,
    buildings: DARK_BUILDINGS,
  },
  {
    name: 'Verdant',
    hues: [
      '#2fd39b',
      '#7ed957',
      '#d8c657',
      '#4fb8d6',
      '#b48ee0',
      '#e08aa4',
    ],
    background: '#08130f',
    panel: '#0f1d18',
    panelAlt: '#14261f',
    border: '#23453a',
    gridLine: '#1a3329',
    text: '#cfe8dc',
    textDim: '#6d907f',
    accent: '#2fd39b',
    accentSoft: '#10362b',
    warning: '#e0b64a',
    danger: '#f2686b',
    ok: '#4fd67f',
    factory: '#d8c657',
    empty: '#10201a',
    queued: '#1d3830',
    building: '#2fd39b',
    demolishing: '#f2686b',
    minerals: DARK_MINERALS,
    buildings: DARK_BUILDINGS,
  },
  {
    name: 'Nebula',
    hues: [
      '#a06cff',
      '#ff5f87',
      '#57d6a8',
      '#f0a93b',
      '#5fa8ff',
      '#e07be0',
    ],
    background: '#0e0a18',
    panel: '#171128',
    panelAlt: '#1e1733',
    border: '#362a55',
    gridLine: '#261d40',
    text: '#ded5f2',
    textDim: '#8a7cae',
    accent: '#a06cff',
    accentSoft: '#2e1d52',
    warning: '#f0a93b',
    danger: '#ff5f87',
    ok: '#57d6a8',
    factory: '#e0b64a',
    empty: '#150f24',
    queued: '#2a2044',
    building: '#a06cff',
    demolishing: '#ff5f87',
    minerals: DARK_MINERALS,
    buildings: DARK_BUILDINGS,
  },
  {
    // The fifth temperature: none of the others are red. Deep oxide against
    // rose and coral, which reads as a warning-lit compartment rather than as
    // Ember's furnace glow.
    name: 'Cinder',
    hues: [
      '#ff4d6d',
      '#ff8a5c',
      '#e8c15a',
      '#6fd4c0',
      '#7d9dff',
      '#d67ae0',
    ],
    background: '#140a0e',
    panel: '#1e1016',
    panelAlt: '#26151d',
    border: '#4a2734',
    gridLine: '#331b24',
    text: '#f0d8de',
    textDim: '#a3798a',
    accent: '#ff4d6d',
    accentSoft: '#4d1424',
    warning: '#e8a13a',
    danger: '#ff5c5c',
    ok: '#5fc98f',
    factory: '#e8c15a',
    empty: '#1c0f14',
    queued: '#3a2029',
    building: '#ff4d6d',
    demolishing: '#ff5c5c',
    minerals: DARK_MINERALS,
    buildings: DARK_BUILDINGS,
  },
];

/**
 * Chrome colours for the canvas renderers. Mutated in place by applyTheme —
 * see the note at the top of the file.
 */
export const COLORS = {
  background: THEMES[0]!.background,
  panel: THEMES[0]!.panel,
  border: THEMES[0]!.border,
  gridLine: THEMES[0]!.gridLine,
  text: THEMES[0]!.text,
  textDim: THEMES[0]!.textDim,
  factory: THEMES[0]!.factory,
  empty: THEMES[0]!.empty,
  queued: THEMES[0]!.queued,
  building: THEMES[0]!.building,
  demolishing: THEMES[0]!.demolishing,
  accent: THEMES[0]!.accent,
  warning: THEMES[0]!.warning,
  danger: THEMES[0]!.danger,
};

export const MINERAL_COLORS: Record<MineralId, string> = { ...DARK_MINERALS };
export const BUILDING_COLORS: Record<BuildingId, string> = { ...DARK_BUILDINGS };

const THEME_KEY = 'heliocenter.theme';

let activeIndex = 0;

function readStoredIndex(): number {
  try {
    const raw = window.localStorage.getItem(THEME_KEY);
    if (raw === null) return 0;
    const found = THEMES.findIndex((theme) => theme.name === raw);
    return found < 0 ? 0 : found;
  } catch {
    return 0;
  }
}

function storeIndex(index: number): void {
  try {
    window.localStorage.setItem(THEME_KEY, THEMES[index]!.name);
  } catch {
    // Storage disabled or full. The choice simply will not be remembered.
  }
}

/** The palette currently in force. */
export function currentTheme(): Palette {
  return THEMES[activeIndex]!;
}

/**
 * Put a theme in force: write its CSS custom properties, and mutate the objects
 * the canvas renderers read. Callers must redraw afterwards — this does not
 * know about the render loop.
 */
export function applyTheme(index: number): Palette {
  const bounded = ((index % THEMES.length) + THEMES.length) % THEMES.length;
  activeIndex = bounded;
  const theme = THEMES[bounded]!;

  const style = document.documentElement.style;
  style.setProperty('--bg', theme.background);
  style.setProperty('--panel', theme.panel);
  style.setProperty('--panel-alt', theme.panelAlt);
  style.setProperty('--border', theme.border);
  style.setProperty('--grid-line', theme.gridLine);
  style.setProperty('--text', theme.text);
  style.setProperty('--text-dim', theme.textDim);
  style.setProperty('--accent', theme.accent);
  style.setProperty('--accent-soft', theme.accentSoft);
  style.setProperty('--warn', theme.warning);
  style.setProperty('--danger', theme.danger);
  style.setProperty('--ok', theme.ok);

  // One numbered variable per screen hue, consumed by the rail keys and the
  // panel headers on each screen. See Palette.hues.
  theme.hues.forEach((hue, index) => {
    style.setProperty(`--hue-${index + 1}`, hue);
  });

  // Swatches reference these by name rather than being painted once at build
  // time, so a theme change reaches swatches that already exist.
  for (const [id, colour] of Object.entries(theme.minerals)) {
    style.setProperty(`--mineral-${id}`, colour);
  }
  for (const [id, colour] of Object.entries(theme.buildings)) {
    style.setProperty(`--building-${id}`, colour);
  }

  COLORS.background = theme.background;
  COLORS.panel = theme.panel;
  COLORS.border = theme.border;
  COLORS.gridLine = theme.gridLine;
  COLORS.text = theme.text;
  COLORS.textDim = theme.textDim;
  COLORS.factory = theme.factory;
  COLORS.empty = theme.empty;
  COLORS.queued = theme.queued;
  COLORS.building = theme.building;
  COLORS.demolishing = theme.demolishing;
  COLORS.accent = theme.accent;
  COLORS.warning = theme.warning;
  COLORS.danger = theme.danger;

  Object.assign(MINERAL_COLORS, theme.minerals);
  Object.assign(BUILDING_COLORS, theme.buildings);

  storeIndex(bounded);
  return theme;
}

/** Restore whatever theme was last chosen. Call once at start-up. */
export function initTheme(): Palette {
  return applyTheme(readStoredIndex());
}

/** Step to the next theme along and return it. */
export function cycleTheme(): Palette {
  return applyTheme(activeIndex + 1);
}

/**
 * Prepare a canvas for crisp drawing on a high-DPI display and return its
 * context with the device-pixel scale already applied, so all drawing code can
 * work in CSS pixels.
 *
 * Returns null when the 2D context is unavailable — a headless or unusual
 * environment. Callers skip drawing rather than throwing.
 */
export function prepareCanvas(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
): CanvasRenderingContext2D | null {
  const ratio = Math.max(1, devicePixelRatio);
  const width = Math.round(cssWidth * ratio);
  const height = Math.round(cssHeight * ratio);

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;

  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, cssWidth, cssHeight);
  return ctx;
}
